import React, { useEffect, useState } from 'react';
import { Button, DateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { MdOutlineRestartAlt, MdSearch } from 'react-icons/md';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import PropTypes from 'prop-types';

const EMPTY = {
  q_number: '',
  j_number: '',
  project_name: '',
  building_name: '',
  building_description: '',
  customer_name: '',
  sales_incharge: '',
  project_type: '',
  down_payment: { startDate: '', endDate: '' },
  purchase_order_date: { startDate: '', endDate: '' },
  down_payment_without_contract: false,
  has_purchase_order: false,
  sales_unit_ids: [],
  sales_regions: [],
};

const paramsToVals = (p = {}) => ({
  q_number:                      p.project_q_number_cont || '',
  j_number:                      p.j_number_cont || '',
  project_name:                  p.project_name_cont || '',
  building_name:                 p.name_cont || '',
  building_description:          p.description_cont || '',
  customer_name:                 p.company_name_cont || '',
  sales_incharge:                p.project_sales_incharge_cont || '',
  // project_type enum: hot_roll = 2, built_up = 1
  project_type:                  [2, '2'].includes(p.project_project_type_eq) ? 'hot_roll'
                               : [1, '1'].includes(p.project_project_type_eq) ? 'built_up' : '',
  down_payment:                  { startDate: p.down_payment_gteq || '', endDate: p.down_payment_lteq || '' },
  purchase_order_date:           { startDate: p.purchase_order_date_gteq || '', endDate: p.purchase_order_date_lteq || '' },
  down_payment_without_contract: !!p.contract_date_null,
  has_purchase_order:            p.contract_note_cont === 'PO',
  sales_unit_ids:                p.sales_unit_id_in || [],
  sales_regions:                 p.sales_unit_region_in || [],
});

const clean = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== '' && v !== null));

const buildParamsAndTags = (vals) => {
  const params = clean({
    project_q_number_cont:        vals.q_number || undefined,
    j_number_cont:                vals.j_number || undefined,
    project_name_cont:            vals.project_name || undefined,
    name_cont:                    vals.building_name || undefined,
    description_cont:             vals.building_description || undefined,
    company_name_cont:            vals.customer_name || undefined,
    project_sales_incharge_cont:  vals.sales_incharge || undefined,
    project_project_type_eq:      vals.project_type === 'hot_roll' ? 2 : vals.project_type === 'built_up' ? 1 : undefined,
    down_payment_gteq:            vals.down_payment.startDate || undefined,
    down_payment_lteq:            vals.down_payment.endDate || undefined,
    purchase_order_date_gteq:     vals.purchase_order_date.startDate || undefined,
    purchase_order_date_lteq:     vals.purchase_order_date.endDate || undefined,
    contract_date_null:           vals.down_payment_without_contract || undefined,
    contract_note_cont:           vals.has_purchase_order ? 'PO' : undefined,
    sales_unit_id_in:             vals.sales_unit_ids.length > 0 ? vals.sales_unit_ids : undefined,
    sales_unit_region_in:         vals.sales_regions.length > 0 ? vals.sales_regions : undefined,
  });

  const fmtRange = (r) =>
    r.startDate && r.endDate ? `${r.startDate} – ${r.endDate}` : null;

  const tags = [
    vals.q_number && { id: 'project_q_number_cont', name: 'Q#', value: vals.q_number },
    vals.j_number && { id: 'j_number_cont', name: 'J#', value: vals.j_number },
    vals.project_name && { id: 'project_name_cont', name: 'Project', value: vals.project_name },
    vals.building_name && { id: 'name_cont', name: 'Building', value: vals.building_name },
    vals.building_description && { id: 'description_cont', name: 'Bldg Desc.', value: vals.building_description },
    vals.customer_name && { id: 'company_name_cont', name: 'Customer', value: vals.customer_name },
    vals.sales_incharge && { id: 'project_sales_incharge_cont', name: 'Sales', value: vals.sales_incharge },
    vals.project_type && { id: 'project_project_type_eq', name: 'Project type', value: vals.project_type === 'hot_roll' ? 'Hot Roll' : 'Built Up' },
    fmtRange(vals.down_payment) && { id: 'down_payment_gteq', name: 'Down Pmt', value: fmtRange(vals.down_payment) },
    fmtRange(vals.purchase_order_date) && { id: 'purchase_order_date_gteq', name: 'PO Date', value: fmtRange(vals.purchase_order_date) },
    vals.down_payment_without_contract && { id: 'contract_date_null', name: 'Flag', value: 'No contract signed' },
    vals.has_purchase_order && { id: 'contract_note_cont', name: 'Flag', value: 'Has PO' },
    vals.sales_unit_ids.length > 0 && { id: 'sales_unit_id_in', name: 'Sales Unit', value: `${vals.sales_unit_ids.length} selected` },
    vals.sales_regions.length > 0 && { id: 'sales_unit_region_in', name: 'Region', value: vals.sales_regions.join(', ') },
  ].filter(Boolean);

  return { params, tags };
};

const LABEL_CLS = 'tw-block tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-mb-1';
import { INPUT_CLS } from 'helpers/formStyles';
const MasterScheduleFilter = ({ show, onClose, onApply, onReset, activeParams = {} }) => {
  const dispatch = useDispatch();
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [vals, setVals] = useState(EMPTY);

  useEffect(() => {
    dispatch(getActiveSalesUnits());
  }, [dispatch]);

  // Re-populate form from current active params whenever the drawer opens.
  // activeParams intentionally omitted from deps — we only want to sync on open.
  useEffect(() => {
    if (show) setVals(paramsToVals(activeParams));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [show, onClose]);

  const salesUnitOptions = activeSalesUnitsData.map((u) => ({ value: u.id, label: u.description }));
  const salesRegionOptions = Array.from(
    new Set(activeSalesUnitsData.filter((u) => u.region).map((u) => u.region))
  ).map((r) => ({ value: r, label: r }));

  const set = (key, value) => setVals((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    const { params, tags } = buildParamsAndTags(vals);
    onApply(params, tags);
  };

  const handleReset = () => {
    setVals(EMPTY);
    onReset();
  };

  if (!show) return null;

  const activeTagCount = buildParamsAndTags(vals).tags.length;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[1200] tw-flex tw-justify-end">
      <div
        className="tw-fixed tw-inset-0 tw-bg-slate-900/20 tw-backdrop-blur-[1px]"
        onClick={onClose}
      />

      <div className="tw-relative tw-w-full tw-max-w-lg tw-bg-white tw-h-full tw-flex tw-flex-col tw-shadow-2xl">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-200 tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-500">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-font-semibold">
            <FaFilter size={13} />
            <span>More Filters</span>
            {activeTagCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[20px] tw-h-5 tw-px-1.5 tw-rounded-xl tw-bg-white/20 tw-text-white tw-text-xs tw-font-bold">
                {activeTagCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-text-white/70 hover:tw-text-white hover:tw-bg-white/20 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors"
          >
            <FaTimes size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4 tw-space-y-4">
          {/* Text fields */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            {[
              ['q_number', 'Q Number'],
              ['j_number', 'J/P Number'],
              ['project_name', 'Project Name'],
              ['building_name', 'Building Name'],
              ['building_description', 'Building Description'],
              ['customer_name', 'Customer Name'],
              ['sales_incharge', 'Sales In-charge'],
            ].map(([k, label]) => (
              <div key={k} className={k === 'building_description' || k === 'project_name' ? 'tw-col-span-2' : ''}>
                <label className={LABEL_CLS}>{label}</label>
                <input
                  type="text"
                  value={vals[k]}
                  onChange={(e) => set(k, e.target.value)}
                  placeholder={label}
                  className={INPUT_CLS}
                />
              </div>
            ))}
          </div>

          {/* Project type */}
          <div>
            <label className={LABEL_CLS}>Project Type</label>
            <select
              value={vals.project_type}
              onChange={(e) => set('project_type', e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">All types</option>
              <option value="hot_roll">Hot Roll</option>
              <option value="built_up">Built Up</option>
            </select>
          </div>

          <div className="tw-border-t tw-border-dashed tw-border-slate-200" />

          {/* Down Payment & Purchase Order date ranges */}
          {[
            ['down_payment', 'Down Payment Date'],
            ['purchase_order_date', 'Purchase Order Date'],
          ].map(([k, label]) => (
            <div key={k}>
              <label className={LABEL_CLS}>{label}</label>
              <DateRangePicker
                label={label}
                startDate={vals[k].startDate}
                endDate={vals[k].endDate}
                onChange={({ startDate, endDate }) => set(k, { startDate, endDate })}
              />
            </div>
          ))}

          {/* Checkboxes */}
          <div className="tw-space-y-2">
            {[
              ['down_payment_without_contract', 'Down Payment without contract signed'],
              ['has_purchase_order', 'Has Purchase Order'],
            ].map(([k, label]) => (
              <label key={k} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-primary-700">
                <input
                  type="checkbox"
                  checked={vals[k]}
                  onChange={(e) => set(k, e.target.checked)}
                  className="tw-accent-primary-600 tw-w-4 tw-h-4"
                />
                {label}
              </label>
            ))}
          </div>

          <div className="tw-border-t tw-border-dashed tw-border-slate-200" />

          {/* Sales unit */}
          <div>
            <label className={LABEL_CLS}>Sales Unit</label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={salesUnitOptions}
              value={salesUnitOptions.filter((o) => vals.sales_unit_ids.includes(o.value))}
              onChange={(selected) => set('sales_unit_ids', (selected || []).map((o) => o.value))}
              placeholder="Select sales units..."
              classNamePrefix="react-select"
            />
          </div>

          {/* Sales regions */}
          <div>
            <label className={LABEL_CLS}>Sales Regions</label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={salesRegionOptions}
              value={salesRegionOptions.filter((o) => vals.sales_regions.includes(o.value))}
              onChange={(selected) => set('sales_regions', (selected || []).map((o) => o.value))}
              placeholder="Select regions..."
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-t tw-border-slate-200 tw-bg-slate-50">
          <span className="tw-text-xs tw-text-slate-400">
            {activeTagCount > 0 ? `${activeTagCount} filter(s) applied` : 'No filters applied'}
          </span>
          <div className="tw-flex tw-gap-2">
            <Button variant="secondary" size="md" leftIcon={FaTimes} onClick={onClose}>
              Close
            </Button>
            <Button
              variant="secondary"
              size="md"
              leftIcon={MdOutlineRestartAlt}
              onClick={handleReset}
              className="tw-text-red-600 tw-border-red-300 hover:tw-bg-red-50"
            >
              Reset
            </Button>
            <Button variant="primary" size="md" leftIcon={MdSearch} onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

MasterScheduleFilter.propTypes = {
  show:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  onApply:      PropTypes.func.isRequired,
  onReset:      PropTypes.func.isRequired,
  activeParams: PropTypes.object,
};

export default MasterScheduleFilter;
